import { useState, useEffect, useCallback } from 'react';
import { reportSchedulesApi, reportsApi, ReportHistoryParams } from '@/api/reports';
import type {
  ReportSchedule,
  ReportScheduleCreate,
  ReportScheduleListItem,
  GeneratedReportListItem,
  ReportGenerateRequest,
} from '@/types/data';
import { useAuthStore } from '@/stores/authStore';
import { FileText, Calendar, Download, Trash2, Play, Clock } from 'lucide-react';

type ViewMode = 'history' | 'schedules' | 'generate';

const REPORT_TYPE_OPTIONS = [
  { value: 'daily', label: '日次レポート' },
  { value: 'weekly', label: '週次レポート' },
  { value: 'monthly', label: '月次レポート' },
];

const PERIOD_TYPE_OPTIONS = [
  { value: 'last_day', label: '前日' },
  { value: 'last_week', label: '先週' },
  { value: 'last_month', label: '先月' },
];

const SECTION_OPTIONS = [
  { value: 'summary', label: 'サマリー' },
  { value: 'kpis', label: 'KPI指標' },
  { value: 'trends', label: 'トレンド' },
  { value: 'charts', label: 'チャート' },
];

const CRON_PRESETS = [
  { value: '0 9 * * *', label: '毎日 9:00' },
  { value: '0 9 * * 1', label: '毎週月曜 9:00' },
  { value: '0 9 1 * *', label: '毎月1日 9:00' },
];

export default function Reports() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('history');

  // レポート履歴
  const [reports, setReports] = useState<GeneratedReportListItem[]>([]);
  const [totalReports, setTotalReports] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [filterReportType, setFilterReportType] = useState<string | undefined>(undefined);

  // スケジュール管理
  const [schedules, setSchedules] = useState<ReportScheduleListItem[]>([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ReportSchedule | null>(null);

  // スケジュール作成/編集フォーム
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleReportType, setScheduleReportType] = useState('weekly');
  const [scheduleCron, setScheduleCron] = useState('0 9 * * 1');
  const [schedulePeriodType, setSchedulePeriodType] = useState('last_week');
  const [scheduleSections, setScheduleSections] = useState<string[]>(['summary', 'kpis', 'trends']);
  const [scheduleDeliveryMethod, setScheduleDeliveryMethod] = useState('email');
  const [scheduleEmailRecipients, setScheduleEmailRecipients] = useState('');

  // レポート生成フォーム
  const [generateReportType, setGenerateReportType] = useState('weekly');
  const [generatePeriodStart, setGeneratePeriodStart] = useState('');
  const [generatePeriodEnd, setGeneratePeriodEnd] = useState('');
  const [generateSections, setGenerateSections] = useState<string[]>(['summary', 'kpis', 'trends', 'charts']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<string | null>(null);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // デフォルト期間を設定
  useEffect(() => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);

    setGeneratePeriodEnd(today.toISOString().split('T')[0]);
    setGeneratePeriodStart(lastWeek.toISOString().split('T')[0]);
  }, []);

  // レポート履歴取得
  const fetchReports = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoadingReports(true);
    try {
      const params: ReportHistoryParams = {
        company_id: selectedCompanyId,
        page: currentPage,
        per_page: 20,
      };
      if (filterReportType) {
        params.report_type = filterReportType;
      }
      const response = await reportsApi.getHistory(params);
      setReports(response.items);
      setTotalReports(response.total);
    } catch (error) {
      console.error('レポート履歴取得エラー:', error);
    } finally {
      setIsLoadingReports(false);
    }
  }, [selectedCompanyId, currentPage, filterReportType]);

  // スケジュール一覧取得
  const fetchSchedules = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoadingSchedules(true);
    try {
      const data = await reportSchedulesApi.getSchedules(selectedCompanyId);
      setSchedules(data);
    } catch (error) {
      console.error('スケジュール取得エラー:', error);
    } finally {
      setIsLoadingSchedules(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (viewMode === 'history') {
      fetchReports();
    } else if (viewMode === 'schedules') {
      fetchSchedules();
    }
  }, [viewMode, fetchReports, fetchSchedules]);

  // レポートダウンロード
  const downloadReport = async (report: GeneratedReportListItem) => {
    try {
      const blob = await reportsApi.downloadReport(report.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${report.report_type}_${report.period_start}_${report.period_end}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('ダウンロードエラー:', error);
      alert('ダウンロードに失敗しました');
    }
  };

  // レポート削除
  const deleteReport = async (reportId: number) => {
    if (!confirm('このレポートを削除しますか？')) return;
    try {
      await reportsApi.deleteReport(reportId);
      fetchReports();
    } catch (error) {
      console.error('レポート削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  // スケジュールモーダルを開く
  const openScheduleModal = async (schedule?: ReportScheduleListItem) => {
    if (schedule) {
      const fullSchedule = await reportSchedulesApi.getSchedule(schedule.id);
      setEditingSchedule(fullSchedule);
      setScheduleName(fullSchedule.name);
      setScheduleReportType(fullSchedule.report_type);
      setScheduleCron(fullSchedule.schedule_cron);
      setSchedulePeriodType(fullSchedule.period_type);
      setScheduleSections(fullSchedule.include_sections);
      setScheduleDeliveryMethod(fullSchedule.delivery_method);
      setScheduleEmailRecipients(fullSchedule.email_recipients.join(', '));
    } else {
      setEditingSchedule(null);
      setScheduleName('');
      setScheduleReportType('weekly');
      setScheduleCron('0 9 * * 1');
      setSchedulePeriodType('last_week');
      setScheduleSections(['summary', 'kpis', 'trends']);
      setScheduleDeliveryMethod('email');
      setScheduleEmailRecipients('');
    }
    setShowScheduleModal(true);
  };

  // スケジュール保存
  const saveSchedule = async () => {
    if (!selectedCompanyId || !scheduleName) return;

    const emailRecipients = scheduleEmailRecipients
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);

    const data: ReportScheduleCreate = {
      company_id: selectedCompanyId,
      name: scheduleName,
      report_type: scheduleReportType,
      schedule_cron: scheduleCron,
      include_sections: scheduleSections,
      period_type: schedulePeriodType,
      delivery_method: scheduleDeliveryMethod,
      email_recipients: emailRecipients,
    };

    try {
      if (editingSchedule) {
        await reportSchedulesApi.updateSchedule(editingSchedule.id, data);
      } else {
        await reportSchedulesApi.createSchedule(data);
      }
      setShowScheduleModal(false);
      fetchSchedules();
    } catch (error) {
      console.error('スケジュール保存エラー:', error);
      alert('スケジュールの保存に失敗しました');
    }
  };

  // スケジュール削除
  const deleteSchedule = async (scheduleId: number) => {
    if (!confirm('このスケジュールを削除しますか？')) return;
    try {
      await reportSchedulesApi.deleteSchedule(scheduleId);
      fetchSchedules();
    } catch (error) {
      console.error('スケジュール削除エラー:', error);
      alert('スケジュールの削除に失敗しました');
    }
  };

  // スケジュール有効/無効切り替え
  const toggleScheduleActive = async (schedule: ReportScheduleListItem) => {
    try {
      await reportSchedulesApi.updateSchedule(schedule.id, { is_active: !schedule.is_active });
      fetchSchedules();
    } catch (error) {
      console.error('スケジュール更新エラー:', error);
    }
  };

  // レポート生成
  const generateReport = async () => {
    if (!selectedCompanyId || !generatePeriodStart || !generatePeriodEnd) return;

    setIsGenerating(true);
    setGenerateResult(null);

    const data: ReportGenerateRequest = {
      company_id: selectedCompanyId,
      report_type: generateReportType,
      period_start: generatePeriodStart,
      period_end: generatePeriodEnd,
      include_sections: generateSections,
    };

    try {
      const result = await reportsApi.generateReport(data);
      setGenerateResult(`レポートを生成しました（${formatFileSize(result.file_size_bytes)}）`);
      // 履歴を更新
      fetchReports();
    } catch (error) {
      console.error('レポート生成エラー:', error);
      setGenerateResult('レポートの生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  };

  // ファイルサイズのフォーマット
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalPages = Math.ceil(totalReports / 20);

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">レポート管理</h1>

      {/* フィルター */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            >
              <option value="">選択してください</option>
              {accessibleCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.display_name || company.name}
                </option>
              ))}
            </select>
          </div>
          {viewMode === 'history' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                レポートタイプ
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={filterReportType || ''}
                onChange={(e) => {
                  setFilterReportType(e.target.value || undefined);
                  setCurrentPage(1);
                }}
              >
                <option value="">すべて</option>
                {REPORT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* タブ切り替え */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'history'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('history')}
        >
          <FileText className="w-4 h-4" />
          レポート履歴
        </button>
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'generate'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('generate')}
        >
          <Play className="w-4 h-4" />
          レポート生成
        </button>
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'schedules'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('schedules')}
        >
          <Calendar className="w-4 h-4" />
          スケジュール
        </button>
      </div>

      {/* レポート履歴ビュー */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">生成済みレポート</h2>
          </div>
          <div className="overflow-x-auto">
            {isLoadingReports ? (
              <div className="p-8 text-center text-gray-500">読み込み中...</div>
            ) : reports.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                レポートがありません。レポート生成タブから作成してください。
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      レポートタイプ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      期間
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      サイズ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ステータス
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      生成日時
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {REPORT_TYPE_OPTIONS.find((o) => o.value === report.report_type)?.label ||
                            report.report_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {report.period_start} ~ {report.period_end}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatFileSize(report.file_size_bytes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            report.status === 'generated'
                              ? 'bg-green-100 text-green-800'
                              : report.status === 'sent'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {report.status === 'generated'
                            ? '生成済み'
                            : report.status === 'sent'
                            ? '送信済み'
                            : '失敗'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(report.created_at).toLocaleString('ja-JP')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          className="text-blue-600 hover:text-blue-900 mr-4"
                          onClick={() => downloadReport(report)}
                          title="ダウンロード"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={() => deleteReport(report.id)}
                          title="削除"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* ページネーション */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200 flex justify-center gap-2">
              <button
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                前へ
              </button>
              <span className="px-3 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                次へ
              </button>
            </div>
          )}
        </div>
      )}

      {/* レポート生成ビュー */}
      {viewMode === 'generate' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">レポートを生成</h2>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  レポートタイプ
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={generateReportType}
                  onChange={(e) => setGenerateReportType(e.target.value)}
                >
                  {REPORT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  開始日
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={generatePeriodStart}
                  onChange={(e) => setGeneratePeriodStart(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  終了日
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={generatePeriodEnd}
                  onChange={(e) => setGeneratePeriodEnd(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                含めるセクション
              </label>
              <div className="flex flex-wrap gap-4">
                {SECTION_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={generateSections.includes(opt.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setGenerateSections([...generateSections, opt.value]);
                        } else {
                          setGenerateSections(generateSections.filter((s) => s !== opt.value));
                        }
                      }}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                onClick={generateReport}
                disabled={isGenerating || !selectedCompanyId}
              >
                {isGenerating ? (
                  <>
                    <Clock className="w-4 h-4 animate-spin" />
                    生成中...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    レポートを生成
                  </>
                )}
              </button>
              {generateResult && (
                <span
                  className={`text-sm ${
                    generateResult.includes('失敗') ? 'text-red-600' : 'text-green-600'
                  }`}
                >
                  {generateResult}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* スケジュール管理ビュー */}
      {viewMode === 'schedules' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">スケジュール一覧</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={() => openScheduleModal()}
              disabled={!selectedCompanyId}
            >
              新規作成
            </button>
          </div>
          <div className="p-4">
            {isLoadingSchedules ? (
              <div className="text-center py-8">読み込み中...</div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                スケジュールがありません。新規作成してください。
              </div>
            ) : (
              <div className="space-y-4">
                {schedules.map((schedule) => (
                  <div key={schedule.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{schedule.name}</h3>
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              schedule.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {schedule.is_active ? '有効' : '無効'}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-600 space-y-1">
                          <p>
                            <span className="font-medium">タイプ:</span>{' '}
                            {REPORT_TYPE_OPTIONS.find((o) => o.value === schedule.report_type)?.label}
                          </p>
                          <p>
                            <span className="font-medium">スケジュール:</span>{' '}
                            {CRON_PRESETS.find((c) => c.value === schedule.schedule_cron)?.label ||
                              schedule.schedule_cron}
                          </p>
                          <p>
                            <span className="font-medium">対象期間:</span>{' '}
                            {PERIOD_TYPE_OPTIONS.find((o) => o.value === schedule.period_type)?.label}
                          </p>
                          {schedule.last_generated_at && (
                            <p className="text-gray-400">
                              最終生成: {new Date(schedule.last_generated_at).toLocaleString('ja-JP')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className={`text-sm ${
                            schedule.is_active
                              ? 'text-yellow-600 hover:text-yellow-800'
                              : 'text-green-600 hover:text-green-800'
                          }`}
                          onClick={() => toggleScheduleActive(schedule)}
                        >
                          {schedule.is_active ? '無効化' : '有効化'}
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          onClick={() => openScheduleModal(schedule)}
                        >
                          編集
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 text-sm"
                          onClick={() => deleteSchedule(schedule.id)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* スケジュール作成/編集モーダル */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {editingSchedule ? 'スケジュール編集' : 'スケジュール作成'}
              </h2>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スケジュール名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  placeholder="例: 週次レポート"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    レポートタイプ
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={scheduleReportType}
                    onChange={(e) => setScheduleReportType(e.target.value)}
                  >
                    {REPORT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    対象期間
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={schedulePeriodType}
                    onChange={(e) => setSchedulePeriodType(e.target.value)}
                  >
                    {PERIOD_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  実行スケジュール
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={scheduleCron}
                  onChange={(e) => setScheduleCron(e.target.value)}
                >
                  {CRON_PRESETS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  含めるセクション
                </label>
                <div className="flex flex-wrap gap-4">
                  {SECTION_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={scheduleSections.includes(opt.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setScheduleSections([...scheduleSections, opt.value]);
                          } else {
                            setScheduleSections(scheduleSections.filter((s) => s !== opt.value));
                          }
                        }}
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  配信方法
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={scheduleDeliveryMethod}
                  onChange={(e) => setScheduleDeliveryMethod(e.target.value)}
                >
                  <option value="email">メール</option>
                  <option value="download">ダウンロードのみ</option>
                </select>
              </div>
              {scheduleDeliveryMethod === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    送信先メールアドレス
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={scheduleEmailRecipients}
                    onChange={(e) => setScheduleEmailRecipients(e.target.value)}
                    placeholder="カンマ区切りで複数指定可"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    例: user1@example.com, user2@example.com
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowScheduleModal(false)}
              >
                キャンセル
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={saveSchedule}
                disabled={!scheduleName}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
